import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Heart, Rss } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import { MutualFriendsLink } from "@/components/social/MutualFriendsModal";
import { PeopleCountLink } from "@/components/social/PeopleListModal";
import api, { fileUrl } from "@/lib/api";
import { useRelationshipStatus } from "@/lib/relationshipStore";

/** Overlapping friend avatars + "+N" + count — used on People cards and the profile Connections card. */
export function ConnectionsStack({ avatars = [], count = 0, max = 4, className = "" }) {
  if (!count) return null;
  const shown = (avatars || []).filter(Boolean).slice(0, max);
  const extra = count - shown.length;
  return (
    <div className={`flex items-center ${className}`} data-testid="connections-stack">
      <div className="flex -space-x-2">
        {shown.map((url, i) => (
          <span key={i} className="inline-block h-7 w-7 overflow-hidden rounded-full bg-secondary ring-2 ring-card">
            <img src={fileUrl(url)} alt="" className="h-full w-full object-cover" />
          </span>
        ))}
        {shown.length === 0 && (
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-secondary ring-2 ring-card">
            <Users className="h-3.5 w-3.5 text-muted-foreground" />
          </span>
        )}
        {extra > 0 && shown.length > 0 && (
          <span className="inline-flex h-7 min-w-[1.75rem] items-center justify-center rounded-full bg-teal-50 px-1 text-[11px] font-[640] text-teal-700 ring-2 ring-card">+{extra}</span>
        )}
      </div>
      <span className="ml-2 text-xs text-muted-foreground">{count.toLocaleString()} connection{count === 1 ? "" : "s"}</span>
    </div>
  );
}

/** LinkedIn-style Connections card for a member's public profile.
 *  Shows the viewer's MUTUAL friends with this member (avatars + a link to the full
 *  list modal), then three counters: friends · followers · fans. Mutual friends are
 *  the viewer's own connections, so nothing here is gated. */
export function ConnectionsCard({ userId, profileType, profileId, targetName }) {
  const { user } = useAuth();
  const [mutual, setMutual] = useState(null);
  // Live fan/follow counts from the shared store so they update instantly when
  // the viewer toggles Follow/Fan (same source as the buttons). The /network/mutual
  // fetch only seeds friends/mutual + a fallback count.
  const rel = useRelationshipStatus(profileType, profileId);
  useEffect(() => {
    let ok = true;
    api.get(`/network/mutual/${userId}`).then(({ data }) => { if (ok) setMutual(data); }).catch(() => {});
    return () => { ok = false; };
  }, [userId]);

  if (!mutual) return null;
  const isSelf = user?.user_id === userId;
  const friendsCount = mutual.friends_count || 0;
  const mutualCount = mutual.mutual_count || 0;
  const fanCount = rel && typeof rel.fan_count === "number" ? rel.fan_count : (mutual.fan_count || 0);
  const followCount = rel && typeof rel.follow_count === "number" ? rel.follow_count : (mutual.follower_count || 0);
  const avatars = (mutual.mutual_people || []).slice(0, 6);
  const who = targetName ? `${targetName}'s` : "Their";

  return (
    <Card className="border-0 p-6" data-testid="profile-connections-card">
      <h3 className="text-sm font-[640] uppercase tracking-wide text-primary">Connections</h3>

      {!isSelf && (
        <div className="mt-3" data-testid="profile-mutual-friends">
          {mutualCount > 0 ? (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <div className="flex -space-x-2">
                {avatars.map((p) => {
                  const av = (
                    <Avatar className="h-9 w-9 ring-2 ring-card transition-transform hover:-translate-y-0.5">
                      {p.headshot_url && <AvatarImage src={fileUrl(p.headshot_url)} className="object-cover" />}
                      <AvatarFallback className="bg-teal-50 text-teal-700">{(p.display_name || "U")[0]}</AvatarFallback>
                    </Avatar>
                  );
                  return p.slug ? (
                    <Link key={p.user_id} to={`/p/${p.slug}`} title={p.display_name} data-testid={`mutual-avatar-${p.slug}`}>{av}</Link>
                  ) : (
                    <span key={p.user_id} title={p.display_name} data-testid={`mutual-avatar-${p.user_id}`}>{av}</span>
                  );
                })}
              </div>
              <MutualFriendsLink targetUserId={userId} targetName={targetName} count={mutualCount} />
            </div>
          ) : (
            <MutualFriendsLink targetUserId={userId} targetName={targetName} count={0} className="text-sm" />
          )}
        </div>
      )}

      <div className={`flex flex-wrap items-center gap-5 text-sm ${!isSelf ? "mt-4 border-t border-border/60 pt-3" : "mt-3"}`} data-testid="profile-counts">
        <PeopleCountLink
          icon={<Users className="h-4 w-4 text-primary" />}
          count={friendsCount} singular="friend" plural="friends"
          title={`${who} friends`}
          loadIds={async () => (await api.get(`/network/friends/${userId}`)).data.user_ids}
          testId="profile-friends-count" wordTestId="open-friends-modal"
        />
        <PeopleCountLink
          icon={<Rss className="h-4 w-4 text-primary" />}
          count={followCount} singular="follower" plural="followers"
          title={`${who} followers`}
          loadIds={async () => (await api.get(`/network/profile-followers`, { params: { target_type: "personal", target_id: profileId, kind: "follow" } })).data.user_ids}
          testId="profile-followers-count" wordTestId="open-followers-modal"
        />
        <PeopleCountLink
          icon={<Heart className="h-4 w-4 text-primary" />}
          count={fanCount} singular="fan" plural="fans"
          title={`${who} fans`}
          loadIds={async () => (await api.get(`/network/profile-followers`, { params: { target_type: "personal", target_id: profileId, kind: "fan" } })).data.user_ids}
          testId="profile-fans-count" wordTestId="open-fans-modal"
        />
      </div>
    </Card>
  );
}
